/* eslint-disable react-hooks/exhaustive-deps */
import {
  Box,
  Icon,
  Text,
  Toast,
  useColorMode,
  useColorStyle,
  useToastManager,
} from '@tonic-one/react';
import {
  ErrorIcon,
  InfoIcon,
  SuccessIcon,
  WarningTriangleIcon,
} from '@tonic-one/react-icons';
import { useEffect } from 'react';

import { ErrorInfo, PositionEnum, SeverityEnum, ToastProps } from '../../types';
import { useTranslation } from 'react-i18next';

const styleProps: { [key in PositionEnum]: any } = {
  'top-left': { pt: '2x', px: '4x' },
  top: { pt: '2x', px: '4x' },
  'top-right': { pt: '2x', px: '4x' },
  'bottom-left': { pb: '2x', px: '4x' },
  bottom: { pb: '2x', px: '4x' },
  'bottom-right': { pb: '2x', px: '4x' },
};

const ToastLayout = (props: any) => {
  const [colorMode] = useColorMode();
  const [colorStyle] = useColorStyle({ colorMode });
  const boxShadow = colorStyle.shadow.thin;

  return (
    <Box
      fontSize="sm"
      lineHeight="sm"
      textAlign="left"
      boxShadow={boxShadow}
      width={320}
      {...props}
    />
  );
};

const severityIcon: { [key in SeverityEnum]: JSX.Element } = {
  success: <Icon as={SuccessIcon} />,
  info: <Icon as={InfoIcon} />,
  error: <Icon as={ErrorIcon} />,
  warning: <Icon as={WarningTriangleIcon} />,
};

const Toaster = () => {
  const toast = useToastManager();
  const { t } = useTranslation();

  function render403Error(error: ErrorInfo) {
    if (error?.data?.traceId) {
      console.error(
        `if you want to show more 403 details, please implement this function. traceId = ${error.data.traceId}`
      );
    }
    return ({ onClose }: any) => {
      return (
        <Box {...styleProps['bottom-right']}>
          <ToastLayout>
            <Toast
              isClosable
              appearance="error"
              onClose={onClose}
              icon={severityIcon.error}
            >
              <Box>
                <Text fontWeight="semibold">{t('common.error.403')}</Text>
              </Box>
            </Toast>
          </ToastLayout>
        </Box>
      );
    };
  }

  const showNotification = (props: ToastProps) => {
    const {
      position = 'bottom-right',
      duration = 5000,
      titleMsg,
      bodyMsg = '',
      severity,
      error,
    } = props;
    let render;
    if (error?.status === 403) {
      render = render403Error(error);
    } else {
      render = ({ onClose }: any) => {
        return (
          <Box {...styleProps[position]}>
            <ToastLayout>
              <Toast
                isClosable
                appearance={severity}
                onClose={onClose}
                icon={severityIcon[severity]}
              >
                {titleMsg && (
                  <Box>
                    <Text
                      fontWeight="semibold"
                      dangerouslySetInnerHTML={{ __html: titleMsg }}
                    />
                  </Box>
                )}
                {bodyMsg && (
                  <Box mr="-9x">
                    <Text>{bodyMsg}</Text>
                  </Box>
                )}
              </Toast>
            </ToastLayout>
          </Box>
        );
      };
    }
    const options = {
      duration,
      placement: position,
    };
    toast(render, options);
  };

  useEffect(() => {
    if (!window.showNotification) {
      window.showNotification = showNotification;
    }
  }, []);

  return null;
};

export default Toaster;
